import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { hasPermission } from '../services/authService';
import { getCategories } from '../services/categoryService';
import { getTradeInCampaign, updateTradeInCampaign } from '../services/campaignService';
import AdminStickersTab from '../components/AdminStickersTab';
import AdminLoanCampaignTab from '../components/AdminLoanCampaignTab';
import './Admin.css';

export default function AdminCampaigns() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('tradein'); // 'tradein' | 'stickers' | 'loan'

  useEffect(() => {
    if (!hasPermission('admin.settings')) {
      navigate('/login');
    }
  }, [navigate]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [campaign, setCampaign] = useState({
    enabled: true,
    name: 'Sill të vjetrën, merr të renë',
    badgeText: 'Fushata: ulje',
    noteTextDesktop: 'Sill të vjetrën, merr të renë: ulja rritet nga {base}% në {full}%. Pajisja dorëzohet gjatë marrjes ose dërgesës së produktit.',
    noteTextMobile: 'Ulja rritet nga {base}% në {full}%. Pajisja dorëzohet gjatë marrjes ose dërgesës.',
    showExcludedNote: true,
    excludedNoteText: 'Nuk përfshihet në fushatën “Sill të vjetrën merr të renë”',
    checkboxLabel: 'Kam pajisje të vjetër për ta dorëzuar',
    paymentNotice: 'Keni deklaruar se do të dorëzoni pajisjen e vjetër. Nëse nuk e dorëzoni, çmimi kthehet në uljen bazë.',
    defaultRates: { baseDiscount: 15, fullDiscount: 30 },
    categoryRates: {},
    productOverrides: {},
    excludedSkus: ['B2TVNL55LG4K55NANO81A6A']
  });

  // SKU addition input & search filter
  const [newSku, setNewSku] = useState('');
  const [skuSearch, setSkuSearch] = useState('');

  // Product override input
  const [overrideSku, setOverrideSku] = useState('');
  const [overrideBase, setOverrideBase] = useState(15);
  const [overrideFull, setOverrideFull] = useState(30);

  // Category Filtering & Pagination State
  const [selectedParentId, setSelectedParentId] = useState('');
  const [selectedSubId, setSelectedSubId] = useState('');
  const [catSearch, setCatSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [expandedCategories, setExpandedCategories] = useState(() => new Set());
  const [viewMode, setViewMode] = useState('hierarchy'); // 'hierarchy' | 'flat'

  useEffect(() => {
    let mounted = true;
    async function loadData() {
      try {
        setLoading(true);
        const [catsRes, campRes] = await Promise.all([
          getCategories().catch(() => []),
          getTradeInCampaign(true).catch(() => null)
        ]);

        if (!mounted) return;

        if (Array.isArray(catsRes)) {
          setCategories(catsRes);
        }

        if (campRes) {
          setCampaign(campRes);
        }
      } catch (err) {
        console.error('Failed to load campaign data:', err);
        toast.error('Dështoi ngarkimi i të dhënave të fushatës');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    loadData();
    return () => { mounted = false; };
  }, []);

  // Process category hierarchy into structured flat list, roots, and lookup map
  const { allCategoriesFlat, rootCategories, categoryMap } = useMemo(() => {
    const flat = [];
    const roots = [];
    const map = new Map();

    function walk(nodes, depth = 0, path = [], parent = null, root = null) {
      if (!Array.isArray(nodes)) return;
      for (const n of nodes) {
        if (!n || !n.id) continue;
        const currentPath = [...path, n.name];
        const isRoot = depth === 0;
        const rootNode = isRoot ? n : root;
        const item = {
          id: String(n.id),
          name: n.name,
          depth,
          parentId: parent ? String(parent.id) : null,
          parentName: parent ? parent.name : null,
          rootId: rootNode ? String(rootNode.id) : String(n.id),
          rootName: rootNode ? rootNode.name : n.name,
          path: currentPath,
          subcategories: Array.isArray(n.subcategories) ? n.subcategories : [],
          raw: n
        };
        flat.push(item);
        map.set(item.id, item);
        if (isRoot) {
          roots.push(item);
        }
        if (n.subcategories && n.subcategories.length) {
          walk(n.subcategories, depth + 1, currentPath, n, rootNode);
        }
      }
    }
    walk(categories);

    // Calculate total descendants count for each category
    for (const item of flat) {
      const descendants = [];
      function gather(subList) {
        if (!Array.isArray(subList)) return;
        for (const s of subList) {
          if (!s || !s.id) continue;
          descendants.push(String(s.id));
          if (s.subcategories && s.subcategories.length) {
            gather(s.subcategories);
          }
        }
      }
      gather(item.subcategories);
      item.descendantIds = descendants;
      item.descendantsCount = descendants.length;
    }

    return { allCategoriesFlat: flat, rootCategories: roots, categoryMap: map };
  }, [categories]);

  // Subcategories options for the dropdown selector
  const availableSubcategoriesForSelect = useMemo(() => {
    if (selectedParentId) {
      return allCategoriesFlat.filter(c => c.rootId === selectedParentId && c.id !== selectedParentId);
    }
    return allCategoriesFlat.filter(c => c.depth > 0);
  }, [allCategoriesFlat, selectedParentId]);

  // Determine inherited rate for any category
  const getInheritedRate = (catId) => {
    const key = String(catId);
    const item = categoryMap.get(key);
    if (!item || !item.parentId) {
      return campaign.defaultRates || { baseDiscount: 15, fullDiscount: 30 };
    }
    const parentId = String(item.parentId);
    if (campaign.categoryRates?.[parentId]) {
      return campaign.categoryRates[parentId];
    }
    const rootId = String(item.rootId);
    if (campaign.categoryRates?.[rootId]) {
      return campaign.categoryRates[rootId];
    }
    return campaign.defaultRates || { baseDiscount: 15, fullDiscount: 30 };
  };

  // Determine current rate state and source
  const getEffectiveCategoryRate = (catId) => {
    const key = String(catId);
    if (campaign.categoryRates && (campaign.categoryRates[key] !== undefined || campaign.categoryRates[catId] !== undefined)) {
      return {
        rate: campaign.categoryRates[key] || campaign.categoryRates[catId],
        isCustom: true,
        source: 'custom'
      };
    }
    const inherited = getInheritedRate(key);
    const item = categoryMap.get(key);
    const hasParentOverride = item?.parentId && (campaign.categoryRates?.[item.parentId] || campaign.categoryRates?.[String(item.parentId)]);
    const hasRootOverride = item?.rootId && (campaign.categoryRates?.[item.rootId] || campaign.categoryRates?.[String(item.rootId)]);
    return {
      rate: inherited,
      isCustom: false,
      source: hasParentOverride ? 'parent' : (hasRootOverride ? 'root' : 'default')
    };
  };

  // Rate change handler
  const handleCategoryRateChange = (catId, tierKey, val) => {
    const key = String(catId);
    const num = Math.max(0, Math.min(100, parseInt(val, 10) || 0));
    setCampaign(prev => {
      const inherited = getInheritedRate(key);
      const current = prev.categoryRates?.[key] || prev.categoryRates?.[catId] || { ...inherited };
      return {
        ...prev,
        categoryRates: {
          ...prev.categoryRates,
          [key]: {
            ...current,
            [tierKey]: num
          }
        }
      };
    });
  };

  // Cascade rates to all descendant subcategories
  const handleApplyToDescendants = (cat) => {
    const item = categoryMap.get(String(cat.id)) || cat;
    const effective = getEffectiveCategoryRate(String(cat.id)).rate;
    const childIds = item.descendantIds || [];

    if (!childIds.length) {
      toast.error('Kjo kategori nuk ka nënkategori.');
      return;
    }

    setCampaign(prev => {
      const nextRates = { ...(prev.categoryRates || {}) };
      for (const cid of childIds) {
        nextRates[String(cid)] = { ...effective };
      }
      return {
        ...prev,
        categoryRates: nextRates
      };
    });

    const b = effective.baseDiscount ?? effective.tier1 ?? 15;
    const f = effective.fullDiscount ?? effective.tier2 ?? 30;
    toast.success(`Normat (Bazë: ${b}%, Plotë: ${f}%) u aplikuan në të gjitha ${childIds.length} nënkategoritë e "${item.name}".`);
  };

  // Reset custom category rates to inherit
  const handleResetCategoryRate = (catId, catName) => {
    const key = String(catId);
    setCampaign(prev => {
      const nextRates = { ...(prev.categoryRates || {}) };
      delete nextRates[key];
      delete nextRates[catId];
      if (!isNaN(Number(catId))) {
        delete nextRates[Number(catId)];
      }
      return {
        ...prev,
        categoryRates: nextRates
      };
    });
    toast.success(`Norma e personalizuar për "${catName}" u hoq (tashmë trashëgon).`);
  };

  // Reset all descendants of a category to inherit
  const handleResetDescendants = (cat) => {
    const item = categoryMap.get(String(cat.id)) || cat;
    const childIds = item.descendantIds || [];
    if (!childIds.length) return;

    setCampaign(prev => {
      const nextRates = { ...(prev.categoryRates || {}) };
      for (const cid of childIds) {
        delete nextRates[String(cid)];
        delete nextRates[cid];
        if (!isNaN(Number(cid))) {
          delete nextRates[Number(cid)];
        }
      }
      return {
        ...prev,
        categoryRates: nextRates
      };
    });

    toast.success(`Normat e personalizuara për të gjitha ${childIds.length} nënkategoritë e "${item.name}" u hoqën (trashëgojnë nga prindi).`);
  };

  // Expand / collapse helpers
  const toggleExpand = (id) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleExpandAll = () => {
    const allWithChildren = allCategoriesFlat
      .filter(c => c.subcategories && c.subcategories.length > 0)
      .map(c => c.id);
    setExpandedCategories(new Set(allWithChildren));
    toast.success('Të gjitha nënkategoritë u hapën.');
  };

  const handleCollapseAll = () => {
    setExpandedCategories(new Set());
    toast.success('Të gjitha nënkategoritë u mbyllën.');
  };

  // Filter categories to display based on mode, search, parent, and subcategory
  const displayList = useMemo(() => {
    const q = catSearch.trim().toLowerCase();

    // 1. If searching, return all matching categories across the entire tree
    if (q) {
      return allCategoriesFlat.filter(c =>
        c.name.toLowerCase().includes(q) ||
        c.id.includes(q) ||
        c.path.some(p => p.toLowerCase().includes(q))
      );
    }

    // 2. If Flat view mode selected, filter and return all categories
    if (viewMode === 'flat') {
      let list = allCategoriesFlat;
      if (selectedParentId) {
        list = list.filter(c => c.rootId === selectedParentId);
      }
      if (selectedSubId) {
        list = list.filter(c => c.id === selectedSubId || c.parentId === selectedSubId);
      }
      return list;
    }

    // 3. Hierarchy view mode:
    if (selectedSubId) {
      // Focus on selected subcategory and its children
      return allCategoriesFlat.filter(c => c.id === selectedSubId || c.parentId === selectedSubId);
    }

    if (selectedParentId) {
      // Direct subcategories of the selected parent category
      const directChildren = allCategoriesFlat.filter(c => c.parentId === selectedParentId);
      if (directChildren.length > 0) return directChildren;
      // If no direct subcategories found, show the item itself
      return allCategoriesFlat.filter(c => c.id === selectedParentId);
    }

    // Default: Root categories
    return rootCategories;
  }, [allCategoriesFlat, rootCategories, catSearch, viewMode, selectedParentId, selectedSubId]);

  // Pagination calculation
  const totalItems = displayList.length;
  const effectivePageSize = pageSize === 0 ? totalItems : pageSize;
  const totalPages = Math.max(1, Math.ceil(totalItems / (effectivePageSize || 1)));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * effectivePageSize;
  const endIndex = Math.min(startIndex + effectivePageSize, totalItems);
  const currentItems = pageSize === 0 ? displayList : displayList.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await updateTradeInCampaign(campaign);
      if (res && res.campaign) {
        setCampaign(res.campaign);
      }
      toast.success('Konfigurimi i fushatës u ruajt me sukses!');
    } catch (err) {
      console.error(err);
      toast.error('Dështoi ruajtja e fushatës: ' + (err.message || 'Server error'));
    } finally {
      setSaving(false);
    }
  };

  const parseSkus = (raw) => {
    if (!raw || typeof raw !== 'string') return [];
    // Split by newlines, commas, semicolons, tabs, and spaces
    const tokens = raw
      .split(/[\r\n,;\t\s]+/)
      .map(s => s.replace(/["']/g, '').trim())
      .filter(Boolean);
    // Deduplicate maintaining case
    const seen = new Set();
    const unique = [];
    for (const t of tokens) {
      const lower = t.toLowerCase();
      if (!seen.has(lower)) {
        seen.add(lower);
        unique.push(t);
      }
    }
    return unique;
  };

  const parsedCandidateSkus = useMemo(() => {
    return parseSkus(newSku);
  }, [newSku]);

  const filteredExcludedSkus = useMemo(() => {
    const list = campaign.excludedSkus || [];
    const q = skuSearch.trim().toLowerCase();
    if (!q) return list;
    return list.filter(s => String(s).toLowerCase().includes(q));
  }, [campaign.excludedSkus, skuSearch]);

  const handleAddSku = (e) => {
    e?.preventDefault();
    const candidateList = parseSkus(newSku);
    if (!candidateList.length) {
      toast.error('Ju lutem vendosni të paktën një kod SKU.');
      return;
    }

    const currentList = Array.isArray(campaign.excludedSkus) ? campaign.excludedSkus : [];
    const currentLowerSet = new Set(currentList.map(s => String(s).trim().toLowerCase()));

    const toAdd = candidateList.filter(s => !currentLowerSet.has(s.toLowerCase()));
    const alreadyCount = candidateList.length - toAdd.length;

    if (!toAdd.length) {
      toast.error(
        candidateList.length === 1
          ? `SKU "${candidateList[0]}" ekziston tashmë në listën e përjashtuar.`
          : `Të gjitha ${candidateList.length} kodet SKU ekzistojnë tashmë në listën e përjashtuar.`
      );
      return;
    }

    setCampaign(prev => ({
      ...prev,
      excludedSkus: [...(prev.excludedSkus || []), ...toAdd]
    }));
    setNewSku('');

    if (toAdd.length === 1) {
      toast.success(`SKU "${toAdd[0]}" u shtua te produktet e përjashtuara.`);
    } else {
      toast.success(
        `${toAdd.length} kode SKU u shtuan me sukses!${alreadyCount > 0 ? ` (${alreadyCount} ishin tashmë në listë)` : ''}`
      );
    }
  };

  const handleRemoveSku = (skuToRemove) => {
    setCampaign(prev => ({
      ...prev,
      excludedSkus: (prev.excludedSkus || []).filter(
        s => String(s).trim().toLowerCase() !== String(skuToRemove).trim().toLowerCase()
      )
    }));
  };

  const handleClearAllExcluded = () => {
    const count = (campaign.excludedSkus || []).length;
    if (count === 0) return;
    if (window.confirm(`A jeni të sigurt që dëshironi të hiqni të gjitha ${count} produktet nga lista e përjashtuar?`)) {
      setCampaign(prev => ({ ...prev, excludedSkus: [] }));
      setSkuSearch('');
      toast.success(`U hoqën të gjitha ${count} kodet SKU nga lista e përjashtuar.`);
    }
  };

  const handleCopyAllExcluded = () => {
    const list = campaign.excludedSkus || [];
    if (!list.length) {
      toast.error('Nuk ka asnjë SKU në listën e përjashtuar për t\'u kopjuar.');
      return;
    }
    navigator.clipboard?.writeText(list.join('\n'));
    toast.success(`${list.length} kode SKU u kopjuan në clipboard (një për çdo rresht).`);
  };

  const handleAddOverride = (e) => {
    e?.preventDefault();
    const clean = overrideSku.trim();
    if (!clean) {
      toast.error('Ju lutem vendosni një SKU ose ID produkti.');
      return;
    }
    setCampaign(prev => ({
      ...prev,
      productOverrides: {
        ...prev.productOverrides,
        [clean]: {
          baseDiscount: Math.max(0, Math.min(100, Number(overrideBase) || 0)),
          fullDiscount: Math.max(0, Math.min(100, Number(overrideFull) || 0))
        }
      }
    }));
    setOverrideSku('');
    toast.success(`Përjashtimi specifik për "${clean}" u shtua.`);
  };

  const handleRemoveOverride = (skuKey) => {
    setCampaign(prev => {
      const copy = { ...prev.productOverrides };
      delete copy[skuKey];
      return { ...prev, productOverrides: copy };
    });
  };

  // Helper for pagination numbers
  function renderPaginationButtons() {
    const buttons = [];
    const maxButtons = 7;
    let start = Math.max(1, safeCurrentPage - 3);
    let end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }

    if (start > 1) {
      buttons.push(
        <button
          key={1}
          type="button"
          onClick={() => handlePageChange(1)}
          style={{
            padding: '5px 10px',
            borderRadius: 6,
            border: '1px solid #cbd5e1',
            background: '#fff',
            color: '#334155',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          1
        </button>
      );
      if (start > 2) {
        buttons.push(<span key="dots1" style={{ padding: '0 4px', color: '#94a3b8' }}>...</span>);
      }
    }

    for (let p = start; p <= end; p++) {
      const isActive = p === safeCurrentPage;
      buttons.push(
        <button
          key={p}
          type="button"
          onClick={() => handlePageChange(p)}
          style={{
            padding: '5px 11px',
            borderRadius: 6,
            border: isActive ? '1px solid #007da5' : '1px solid #cbd5e1',
            background: isActive ? '#007da5' : '#fff',
            color: isActive ? '#fff' : '#334155',
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            minWidth: 32
          }}
        >
          {p}
        </button>
      );
    }

    if (end < totalPages) {
      if (end < totalPages - 1) {
        buttons.push(<span key="dots2" style={{ padding: '0 4px', color: '#94a3b8' }}>...</span>);
      }
      buttons.push(
        <button
          key={totalPages}
          type="button"
          onClick={() => handlePageChange(totalPages)}
          style={{
            padding: '5px 10px',
            borderRadius: 6,
            border: '1px solid #cbd5e1',
            background: '#fff',
            color: '#334155',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          {totalPages}
        </button>
      );
    }

    return buttons;
  }

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-header-flex">
          <h2>Fushatat</h2>
        </div>
        <div style={{ padding: '40px 0', textAlign: 'center', color: '#64748b' }}>
          Duke ngarkuar konfigurimin e fushatës...
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header-flex" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#0f172a' }}>Fushatat</h2>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
            {activeTab === 'stickers'
              ? 'Konfigurimi dhe pozicionimi i etiketave promocionale (Stickers) mbi kartat e produkteve.'
              : activeTab === 'loan'
              ? 'Konfigurimi i kredisë dhe blerjes me këste (12 / 36 muaj) sipas kategorive dhe kodeve SKU.'
              : 'Menaxhimi i ofertës "Sill të vjetrën, merr të renë", zbritjeve me 3 nivele dhe produkteve të përjashtuara.'}
          </p>
        </div>
        {activeTab === 'tradein' && (
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              background: '#007da5',
              color: '#fff',
              border: 'none',
              padding: '10px 24px',
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
              boxShadow: '0 2px 8px rgba(0,125,165,0.25)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            {saving ? 'Duke ruajtur...' : 'Ruaj Ndryshimet'}
          </button>
        )}
      </div>

      {/* Tabs Header Navigation */}
      <div style={{ display: 'flex', gap: 8, borderBottom: '2px solid #e2e8f0', marginBottom: 24 }}>
        <button
          type="button"
          onClick={() => setActiveTab('tradein')}
          style={{
            padding: '12px 22px',
            border: 'none',
            borderBottom: activeTab === 'tradein' ? '3px solid #007da5' : '3px solid transparent',
            background: 'transparent',
            color: activeTab === 'tradein' ? '#007da5' : '#64748b',
            fontWeight: 700,
            fontSize: 15,
            cursor: 'pointer',
            marginBottom: -2,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            transition: 'all 0.15s ease'
          }}
        >
          <span>♻️</span>
          <span>Sill të vjetrën, merr të renë</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('stickers')}
          style={{
            padding: '12px 22px',
            border: 'none',
            borderBottom: activeTab === 'stickers' ? '3px solid #007da5' : '3px solid transparent',
            background: 'transparent',
            color: activeTab === 'stickers' ? '#007da5' : '#64748b',
            fontWeight: 700,
            fontSize: 15,
            cursor: 'pointer',
            marginBottom: -2,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            transition: 'all 0.15s ease'
          }}
        >
          <span>🏷️</span>
          <span>Stickers (Etiketat e Produkteve)</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('loan')}
          style={{
            padding: '12px 22px',
            border: 'none',
            borderBottom: activeTab === 'loan' ? '3px solid #007da5' : '3px solid transparent',
            background: 'transparent',
            color: activeTab === 'loan' ? '#007da5' : '#64748b',
            fontWeight: 700,
            fontSize: 15,
            cursor: 'pointer',
            marginBottom: -2,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            transition: 'all 0.15s ease'
          }}
        >
          <span>💳</span>
          <span>Kredi / Këste (12 / 36 Muaj)</span>
        </button>
      </div>

      {activeTab === 'loan' ? (
        <AdminLoanCampaignTab categories={categories} />
      ) : activeTab === 'stickers' ? (
        <AdminStickersTab categories={categories} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 24 }}>

        {/* Card 1: Status & General Config */}
        <div className="admin-card" style={{ padding: 24, background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#1e293b' }}>
              ♻️ Statusi &amp; Informacioni i Fushatës
            </h3>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={Boolean(campaign.enabled)}
                onChange={(e) => setCampaign(prev => ({ ...prev, enabled: e.target.checked }))}
                style={{ width: 18, height: 18, accentColor: '#007da5', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 700, fontSize: 14, color: campaign.enabled ? '#15803d' : '#94a3b8' }}>
                {campaign.enabled ? 'Fushata është AKTIVE' : 'Fushata është JO-AKTIVE'}
              </span>
            </label>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                Emri i Fushatës
              </label>
              <input
                type="text"
                className="admin-input"
                value={campaign.name || ''}
                onChange={(e) => setCampaign(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Sill të vjetrën, merr të renë"
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                Teksti i Checkbox-it në Shportë
              </label>
              <input
                type="text"
                className="admin-input"
                value={campaign.checkboxLabel || ''}
                onChange={(e) => setCampaign(prev => ({ ...prev, checkboxLabel: e.target.value }))}
                placeholder="Kam pajisje të vjetër për ta dorëzuar"
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                Teksti për Produktet e Përjashtuara
              </label>
              <input
                type="text"
                className="admin-input"
                value={campaign.excludedNoteText !== undefined ? campaign.excludedNoteText : 'Nuk përfshihet në fushatën “Sill të vjetrën merr të renë”'}
                onChange={(e) => setCampaign(prev => ({ ...prev, excludedNoteText: e.target.value }))}
                placeholder="Nuk përfshihet në fushatën “Sill të vjetrën merr të renë”"
              />
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={campaign.showExcludedNote !== false}
                onChange={(e) => setCampaign(prev => ({ ...prev, showExcludedNote: e.target.checked }))}
                style={{ width: 16, height: 16, accentColor: '#007da5', cursor: 'pointer' }}
              />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
                Shfaq njoftimin kur produkti nuk përfshihet në fushatë (në faqen e produktit &amp; shportë)
              </span>
            </label>
          </div>

          <div style={{ marginTop: 18, padding: 14, background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 8 }}>
              Përqindjet Standarde (Default Rates) kur kategoria nuk ka normë të veçantë:
            </div>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#15803d' }}>Ulja Bazë (pa pajisje):</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  style={{ width: 68, padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e1', textAlign: 'center', fontWeight: 700, color: '#15803d' }}
                  value={campaign.defaultRates?.baseDiscount ?? campaign.defaultRates?.tier1 ?? 15}
                  onChange={(e) => {
                    const v = Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0));
                    setCampaign(prev => ({
                      ...prev,
                      defaultRates: { ...(prev.defaultRates || {}), baseDiscount: v }
                    }));
                  }}
                />
                <span style={{ fontWeight: 700, color: '#64748b' }}>%</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#0369a1' }}>Ulja e Plotë (me pajisje):</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  style={{ width: 68, padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e1', textAlign: 'center', fontWeight: 700, color: '#0369a1' }}
                  value={campaign.defaultRates?.fullDiscount ?? campaign.defaultRates?.tier2 ?? 30}
                  onChange={(e) => {
                    const v = Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0));
                    setCampaign(prev => ({
                      ...prev,
                      defaultRates: { ...(prev.defaultRates || {}), fullDiscount: v }
                    }));
                  }}
                />
                <span style={{ fontWeight: 700, color: '#64748b' }}>%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: Excluded SKUs Manager (Bulk Support) */}
        <div className="admin-card" style={{ padding: 24, background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>🚫 Produktet e Përjashtuara nga Fushata (SKU)</span>
                <span style={{ fontSize: 12, fontWeight: 700, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', padding: '2px 8px', borderRadius: 12 }}>
                  {(campaign.excludedSkus || []).length} produkte
                </span>
              </h3>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b' }}>
                Produktet me këto kode SKU nuk do të kenë zbritje fushate dhe nuk do të kenë opsionin e pajisjes së vjetër. Në shportë shfaqet etiketa:
                <strong style={{ color: '#0f172a' }}> "Nuk përfshihet në fushatën “Sill të vjetrën merr të renë”"</strong>.
              </p>
            </div>
          </div>

          {/* Bulk Input Form */}
          <form onSubmit={handleAddSku} style={{ marginTop: 14, marginBottom: 16 }}>
            <div style={{ position: 'relative' }}>
              <textarea
                rows={3}
                className="admin-input"
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  fontFamily: 'monospace',
                  fontSize: 13,
                  lineHeight: '1.5',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  resize: 'vertical'
                }}
                placeholder="Vendosni një ose më shumë kode SKU (të ndara me rresht të ri, presje, ose hapësirë). Mund të ngjisni (Paste) direkt një kolonë me 100+ SKU nga Excel..."
                value={newSku}
                onChange={(e) => setNewSku(e.target.value)}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 8 }}>
              <div style={{ fontSize: 12, color: parsedCandidateSkus.length > 0 ? '#15803d' : '#64748b', fontWeight: parsedCandidateSkus.length > 0 ? 600 : 400 }}>
                {parsedCandidateSkus.length > 0 ? (
                  <span>✨ {parsedCandidateSkus.length} kode SKU të identifikuara gati për t'u shtuar</span>
                ) : (
                  <span>💡 Këshillë: Mund të kopjoni dhe ngjisni deri në qindra SKU njëherësh nga Excel ose ERP.</span>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                {newSku.trim() && (
                  <button
                    type="button"
                    onClick={() => setNewSku('')}
                    style={{
                      background: '#f8fafc',
                      color: '#64748b',
                      border: '1px solid #cbd5e1',
                      padding: '7px 14px',
                      borderRadius: 8,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Pastro
                  </button>
                )}
                <button
                  type="submit"
                  disabled={!parsedCandidateSkus.length}
                  style={{
                    background: parsedCandidateSkus.length ? '#007da5' : '#94a3b8',
                    color: '#fff',
                    border: 'none',
                    padding: '7px 20px',
                    borderRadius: 8,
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: parsedCandidateSkus.length ? 'pointer' : 'not-allowed',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    transition: 'background 0.15s ease'
                  }}
                >
                  <span>+ Shto {parsedCandidateSkus.length > 1 ? `${parsedCandidateSkus.length} SKU (Masivisht)` : 'SKU'}</span>
                </button>
              </div>
            </div>
          </form>

          {/* Excluded SKUs List Toolbar */}
          {(campaign.excludedSkus || []).length > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 10,
              padding: '10px 14px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              marginBottom: 12
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 220 }}>
                <input
                  type="text"
                  placeholder="🔍 Kërko SKU në listë..."
                  value={skuSearch}
                  onChange={(e) => setSkuSearch(e.target.value)}
                  style={{
                    padding: '5px 10px',
                    fontSize: 12.5,
                    border: '1px solid #cbd5e1',
                    borderRadius: 6,
                    width: 220,
                    background: '#fff'
                  }}
                />
                {skuSearch && (
                  <button
                    type="button"
                    onClick={() => setSkuSearch('')}
                    style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 13, padding: 0 }}
                  >
                    ✕
                  </button>
                )}
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  {skuSearch ? (
                    <span>Po shfaqen <strong>{filteredExcludedSkus.length}</strong> nga <strong>{(campaign.excludedSkus || []).length}</strong></span>
                  ) : (
                    <span>Gjithsej: <strong>{(campaign.excludedSkus || []).length}</strong> SKU</span>
                  )}
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  type="button"
                  onClick={handleCopyAllExcluded}
                  title="Kopjo të gjitha kodet SKU në clipboard (një për rresht)"
                  style={{
                    background: '#fff',
                    color: '#334155',
                    border: '1px solid #cbd5e1',
                    padding: '4px 10px',
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  📋 Kopjo të gjitha
                </button>
                <button
                  type="button"
                  onClick={handleClearAllExcluded}
                  title="Fshij të gjitha SKU nga lista e përjashtuar"
                  style={{
                    background: '#fff',
                    color: '#dc2626',
                    border: '1px solid #fecaca',
                    padding: '4px 10px',
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  🗑️ Pastro listën
                </button>
              </div>
            </div>
          )}

          {/* Badges Container */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            maxHeight: (campaign.excludedSkus || []).length > 20 ? 300 : 'auto',
            overflowY: (campaign.excludedSkus || []).length > 20 ? 'auto' : 'visible',
            padding: (campaign.excludedSkus || []).length > 20 ? 8 : 0,
            border: (campaign.excludedSkus || []).length > 20 ? '1px solid #f1f5f9' : 'none',
            borderRadius: 8
          }}>
            {(campaign.excludedSkus || []).length === 0 ? (
              <div style={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic', padding: '8px 0' }}>
                Nuk ka asnjë produkt të përjashtuar. Të gjitha produktet janë pjesë e fushatës.
              </div>
            ) : filteredExcludedSkus.length === 0 ? (
              <div style={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic', padding: '8px 0' }}>
                Nuk u gjet asnjë SKU që përputhet me "{skuSearch}".
              </div>
            ) : (
              filteredExcludedSkus.map((sku) => (
                <div
                  key={sku}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    color: '#991b1b',
                    padding: '5px 12px',
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: 'monospace'
                  }}
                >
                  <span>{sku}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveSku(sku)}
                    title={`Hiq "${sku}" nga lista e përjashtuar`}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#ef4444',
                      cursor: 'pointer',
                      fontSize: 13,
                      lineHeight: 1,
                      padding: '0 2px',
                      fontWeight: 700
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Card 3: Category Tier Rates Configuration Table with Dropdown & Pagination */}
        <div className="admin-card" style={{ padding: 24, background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0' }}>
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#1e293b' }}>
              📊 Zbritja e Ndërrimit sipas Kategorive (Ulja Bazë dhe Ulja e Plotë)
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
              Përdorni menynë dropdown për të zgjedhur kategorinë kryesore ose nënkategorinë, shfletoni me faqosje (pagination), ose zgjeroni nënkategoritë sipas dëshirës.
            </p>
          </div>

          {/* Filter & Dropdown Toolbar */}
          <div style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 12,
            padding: '14px 16px',
            marginBottom: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 14
          }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-end', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
                
                {/* 1. Parent Category Dropdown */}
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 4 }}>
                    Kategoria Kryesore:
                  </label>
                  <select
                    value={selectedParentId}
                    onChange={(e) => {
                      setSelectedParentId(e.target.value);
                      setSelectedSubId('');
                      setCurrentPage(1);
                    }}
                    style={{
                      padding: '7px 12px',
                      borderRadius: 8,
                      border: '1px solid #cbd5e1',
                      fontSize: 13,
                      fontWeight: 600,
                      background: '#fff',
                      color: '#0f172a',
                      minWidth: 220,
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">Të gjitha kategoritë kryesore ({rootCategories.length})</option>
                    {rootCategories.map(rc => (
                      <option key={rc.id} value={rc.id}>
                        {rc.name} ({rc.descendantsCount} nënkategori)
                      </option>
                    ))}
                  </select>
                </div>

                {/* 2. Subcategory Dropdown */}
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 4 }}>
                    Nënkategoria:
                  </label>
                  <select
                    value={selectedSubId}
                    onChange={(e) => {
                      setSelectedSubId(e.target.value);
                      setCurrentPage(1);
                    }}
                    style={{
                      padding: '7px 12px',
                      borderRadius: 8,
                      border: '1px solid #cbd5e1',
                      fontSize: 13,
                      fontWeight: 600,
                      background: '#fff',
                      color: '#0f172a',
                      minWidth: 240,
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">
                      {selectedParentId
                        ? `Të gjitha nënkategoritë e "${categoryMap.get(selectedParentId)?.name || 'kësaj'}"`
                        : 'Zgjidh një nënkategori...'}
                    </option>
                    {availableSubcategoriesForSelect.map(sc => (
                      <option key={sc.id} value={sc.id}>
                        {sc.depth > 1 ? `↳ ${sc.name}` : sc.name} (ID: {sc.id})
                      </option>
                    ))}
                  </select>
                </div>

                {/* 3. Expand / Collapse All Toggles */}
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 4 }}>
                    Zgjero / Mbyll Nënkategoritë:
                  </label>
                  <div style={{ display: 'inline-flex', gap: 6 }}>
                    <button
                      type="button"
                      onClick={handleExpandAll}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 6,
                        border: '1px solid #cbd5e1',
                        background: '#fff',
                        color: '#334155',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      ▾ Zgjero të Gjitha
                    </button>
                    <button
                      type="button"
                      onClick={handleCollapseAll}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 6,
                        border: '1px solid #cbd5e1',
                        background: '#fff',
                        color: '#334155',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      ▸ Mbyll të Gjitha
                    </button>
                  </div>
                </div>

              </div>

              {/* Search & View Mode Switcher */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 4 }}>
                    Kërko:
                  </label>
                  <input
                    type="text"
                    placeholder="Kërko me emër ose ID..."
                    value={catSearch}
                    onChange={(e) => {
                      setCatSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                    style={{
                      width: 200,
                      padding: '7px 12px',
                      borderRadius: 8,
                      border: '1px solid #cbd5e1',
                      fontSize: 13,
                      background: '#fff'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 4 }}>
                    Mënyra:
                  </label>
                  <div style={{ display: 'flex', border: '1px solid #cbd5e1', borderRadius: 8, overflow: 'hidden' }}>
                    <button
                      type="button"
                      onClick={() => { setViewMode('hierarchy'); setCurrentPage(1); }}
                      style={{
                        padding: '6px 12px',
                        border: 'none',
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: 'pointer',
                        background: viewMode === 'hierarchy' ? '#007da5' : '#fff',
                        color: viewMode === 'hierarchy' ? '#fff' : '#64748b'
                      }}
                    >
                      Hierarki
                    </button>
                    <button
                      type="button"
                      onClick={() => { setViewMode('flat'); setCurrentPage(1); }}
                      style={{
                        padding: '6px 12px',
                        border: 'none',
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: 'pointer',
                        background: viewMode === 'flat' ? '#007da5' : '#fff',
                        color: viewMode === 'flat' ? '#fff' : '#64748b'
                      }}
                    >
                      Lista e Plotë ({allCategoriesFlat.length})
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Active filter summary pill */}
            {(selectedParentId || selectedSubId || catSearch) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#0369a1', background: '#e0f2fe', padding: '6px 12px', borderRadius: 6 }}>
                <span>🔎 Filtrim Aktiv:</span>
                {selectedParentId && (
                  <span>Prindi: <strong>{categoryMap.get(selectedParentId)?.name}</strong></span>
                )}
                {selectedSubId && (
                  <span>Nënkategoria: <strong>{categoryMap.get(selectedSubId)?.name}</strong></span>
                )}
                {catSearch && (
                  <span>Termi: <strong>"{catSearch}"</strong></span>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setSelectedParentId('');
                    setSelectedSubId('');
                    setCatSearch('');
                    setCurrentPage(1);
                  }}
                  style={{
                    marginLeft: 'auto',
                    background: 'transparent',
                    border: 'none',
                    color: '#0284c7',
                    fontWeight: 700,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontSize: 12
                  }}
                >
                  Pastro të gjithë filtrat
                </button>
              </div>
            )}
          </div>

          {/* Context Banner when viewing a specific Parent Category */}
          {selectedParentId && !catSearch && !selectedSubId && viewMode === 'hierarchy' && (() => {
            const parentNode = categoryMap.get(selectedParentId);
            if (!parentNode) return null;
            const parentEffective = getEffectiveCategoryRate(selectedParentId);
            return (
              <div style={{
                background: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: 10,
                padding: '12px 18px',
                marginBottom: 16,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 12
              }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#0284c7', letterSpacing: 0.5 }}>
                    Kategoria Prind Kryesore
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#0369a1' }}>
                    {parentNode.name} <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>(ID: {parentNode.id})</span>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#0369a1' }}>Norma Prind:</span>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={parentEffective.rate.baseDiscount ?? parentEffective.rate.tier1 ?? 15}
                      onChange={(e) => handleCategoryRateChange(selectedParentId, 'baseDiscount', e.target.value)}
                      title="Ulja Bazë (%)"
                      style={{ width: 52, padding: '4px 6px', textAlign: 'center', fontWeight: 700, borderRadius: 6, border: '1px solid #93c5fd', color: '#15803d', background: '#fff' }}
                    />
                    <span style={{ fontSize: 11, color: '#64748b' }}>Bazë /</span>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={parentEffective.rate.fullDiscount ?? parentEffective.rate.tier2 ?? 30}
                      onChange={(e) => handleCategoryRateChange(selectedParentId, 'fullDiscount', e.target.value)}
                      title="Ulja e Plotë (%)"
                      style={{ width: 52, padding: '4px 6px', textAlign: 'center', fontWeight: 700, borderRadius: 6, border: '1px solid #93c5fd', color: '#0369a1', background: '#fff' }}
                    />
                    <span style={{ fontSize: 11, color: '#64748b' }}>Plotë %</span>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleApplyToDescendants(parentNode)}
                    style={{
                      background: '#007da5',
                      color: '#fff',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    ⚡ Apliko në {parentNode.descendantsCount} nënkategori
                  </button>
                </div>
              </div>
            );
          })()}

          {/* Categories Table */}
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table" style={{ width: '100%', minWidth: 700 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', width: '38%' }}>Kategoria</th>
                  <th style={{ textAlign: 'center', width: '16%' }}>Nënkategoritë</th>
                  <th style={{ textAlign: 'center', width: '18%' }}>Ulja Bazë (%)</th>
                  <th style={{ textAlign: 'center', width: '18%' }}>Ulja e Plotë (%)</th>
                  <th style={{ textAlign: 'center', width: '10%' }}>Veprime</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '32px 16px', color: '#94a3b8' }}>
                      Nuk u gjet asnjë kategori me filtrat e zgjedhur.
                    </td>
                  </tr>
                ) : (
                  currentItems.map((cat) => {
                    const item = categoryMap.get(String(cat.id)) || cat;
                    const effective = getEffectiveCategoryRate(String(cat.id));
                    const isExpanded = expandedCategories.has(String(cat.id));
                    const hasSubcategories = Array.isArray(cat.subcategories) && cat.subcategories.length > 0;

                    return (
                      <React.Fragment key={cat.id}>
                        <tr style={{ background: isExpanded ? '#f8fafc' : '#fff' }}>
                          {/* 1. Category Column */}
                          <td style={{ paddingLeft: `${(cat.depth || 0) * 16 + 12}px` }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              {cat.depth > 0 && <span style={{ color: '#94a3b8' }}>↳</span>}
                              <div>
                                <span style={{ fontWeight: cat.depth === 0 ? 700 : 600, color: cat.depth === 0 ? '#0f172a' : '#1e293b' }}>
                                  {cat.name}
                                </span>
                                <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 6 }}>
                                  (ID: {cat.id})
                                </span>
                                {cat.path && cat.path.length > 1 && viewMode === 'flat' && (
                                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                                    {cat.path.slice(0, -1).join(' > ')}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* 2. Subcategories Column / Dropdown Toggle */}
                          <td style={{ textAlign: 'center' }}>
                            {hasSubcategories ? (
                              <button
                                type="button"
                                onClick={() => toggleExpand(String(cat.id))}
                                style={{
                                  background: isExpanded ? '#e0f2fe' : '#f1f5f9',
                                  border: `1px solid ${isExpanded ? '#bae6fd' : '#cbd5e1'}`,
                                  color: isExpanded ? '#0369a1' : '#334155',
                                  borderRadius: 6,
                                  padding: '4px 10px',
                                  fontSize: 12,
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 6,
                                  transition: 'all 0.15s ease'
                                }}
                              >
                                <span>{isExpanded ? '▼' : '▶'}</span>
                                <span>{cat.subcategories.length} Nënkategori</span>
                              </button>
                            ) : (
                              <span style={{ fontSize: 11, color: '#cbd5e1' }}>—</span>
                            )}
                          </td>

                          {/* 3. Base Discount Rate */}
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={effective.rate.baseDiscount ?? effective.rate.tier1 ?? 15}
                              onChange={(e) => handleCategoryRateChange(String(cat.id), 'baseDiscount', e.target.value)}
                              title="Ulja Bazë (pa pajisje të vjetër)"
                              style={{
                                width: 56,
                                padding: '4px 6px',
                                border: `1px solid ${effective.isCustom ? '#15803d' : '#cbd5e1'}`,
                                borderRadius: 6,
                                textAlign: 'center',
                                fontWeight: 700,
                                color: '#15803d',
                                background: effective.isCustom ? '#f0fdf4' : '#fff'
                              }}
                            />
                          </td>

                          {/* 4. Full Discount Rate */}
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={effective.rate.fullDiscount ?? effective.rate.tier2 ?? 30}
                              onChange={(e) => handleCategoryRateChange(String(cat.id), 'fullDiscount', e.target.value)}
                              title="Ulja e Plotë (me pajisje të vjetër)"
                              style={{
                                width: 56,
                                padding: '4px 6px',
                                border: `1px solid ${effective.isCustom ? '#0369a1' : '#cbd5e1'}`,
                                borderRadius: 6,
                                textAlign: 'center',
                                fontWeight: 700,
                                color: '#0369a1',
                                background: effective.isCustom ? '#f0f9ff' : '#fff'
                              }}
                            />
                          </td>

                          {/* 5. Actions & Status */}
                          <td style={{ textAlign: 'center' }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              {hasSubcategories && (
                                <button
                                  type="button"
                                  onClick={() => handleApplyToDescendants(cat)}
                                  title={`Apliko normat e "${cat.name}" në të gjitha nënkategoritë e saj`}
                                  style={{
                                    background: '#f0fdf4',
                                    border: '1px solid #bbf7d0',
                                    color: '#15803d',
                                    borderRadius: 4,
                                    padding: '3px 6px',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    cursor: 'pointer'
                                  }}
                                >
                                  ⚡
                                </button>
                              )}
                              {effective.isCustom ? (
                                <button
                                  type="button"
                                  onClick={() => handleResetCategoryRate(String(cat.id), cat.name)}
                                  title="Hiq normën e personalizuar dhe trashëgo nga prindi/default"
                                  style={{
                                    background: '#fef2f2',
                                    border: '1px solid #fecaca',
                                    color: '#dc2626',
                                    borderRadius: 4,
                                    padding: '3px 6px',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    cursor: 'pointer'
                                  }}
                                >
                                  ✕
                                </button>
                              ) : (
                                <span style={{ fontSize: 10, color: '#94a3b8' }}>Auto</span>
                              )}
                            </div>
                          </td>
                        </tr>

                        {/* Expandable Subcategories Accordion / Dropdown Panel */}
                        {hasSubcategories && isExpanded && (
                          <tr>
                            <td colSpan={5} style={{ padding: 0, background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                              <div style={{ padding: '14px 18px 18px', marginLeft: 24, borderLeft: '3px solid #007da5' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                                    📂 Nënkategoritë e "{cat.name}" ({cat.subcategories.length} nënkategori direkte, {item.descendantsCount} gjithsej):
                                  </div>
                                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <button
                                      type="button"
                                      onClick={() => handleApplyToDescendants(cat)}
                                      style={{
                                        background: '#e0f2fe',
                                        color: '#0369a1',
                                        border: '1px solid #bae6fd',
                                        padding: '4px 10px',
                                        borderRadius: 6,
                                        fontSize: 12,
                                        fontWeight: 600,
                                        cursor: 'pointer'
                                      }}
                                    >
                                      ⚡ Apliko normat e prindit ({effective.rate.baseDiscount ?? effective.rate.tier1 ?? 15}% / {effective.rate.fullDiscount ?? effective.rate.tier2 ?? 30}%) te të gjitha nënkategoritë
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleResetDescendants(cat)}
                                      title="Hiq të gjitha normat e personalizuara nga nënkategoritë e këtij prindi në mënyrë që të trashëgojnë normën"
                                      style={{
                                        background: '#fef2f2',
                                        color: '#dc2626',
                                        border: '1px solid #fecaca',
                                        padding: '4px 10px',
                                        borderRadius: 6,
                                        fontSize: 12,
                                        fontWeight: 600,
                                        cursor: 'pointer'
                                      }}
                                    >
                                      ✕ Rivendos të gjitha nënkategoritë
                                    </button>
                                  </div>
                                </div>

                                <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                    <thead>
                                      <tr style={{ background: '#f1f5f9', borderBottom: '1px solid #cbd5e1' }}>
                                        <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: '#475569' }}>Nënkategoria</th>
                                        <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600, color: '#475569', width: 120 }}>Ulja Bazë (%)</th>
                                        <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600, color: '#475569', width: 120 }}>Ulja e Plotë (%)</th>
                                        <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600, color: '#475569', width: 140 }}>Statusi / Veprime</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {cat.subcategories.map(sub => {
                                        const subItem = categoryMap.get(String(sub.id)) || sub;
                                        const subEffective = getEffectiveCategoryRate(String(sub.id));
                                        const subExpanded = expandedCategories.has(String(sub.id));
                                        const hasSubChildren = Array.isArray(subItem.subcategories) && subItem.subcategories.length > 0;

                                        return (
                                          <React.Fragment key={sub.id}>
                                            <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                                              <td style={{ padding: '8px 12px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                  {hasSubChildren && (
                                                    <button
                                                      type="button"
                                                      onClick={() => toggleExpand(String(sub.id))}
                                                      style={{
                                                        background: subExpanded ? '#e0f2fe' : '#f1f5f9',
                                                        border: `1px solid ${subExpanded ? '#bae6fd' : '#cbd5e1'}`,
                                                        color: subExpanded ? '#0369a1' : '#475569',
                                                        borderRadius: 4,
                                                        padding: '2px 6px',
                                                        fontSize: 11,
                                                        fontWeight: 600,
                                                        cursor: 'pointer'
                                                      }}
                                                    >
                                                      {subExpanded ? '▼' : '▶'} {subItem.subcategories.length}
                                                    </button>
                                                  )}
                                                  <span style={{ fontWeight: 600, color: '#1e293b' }}>
                                                    ↳ {sub.name}
                                                  </span>
                                                  <span style={{ fontSize: 11, color: '#94a3b8' }}>
                                                    (ID: {sub.id})
                                                  </span>
                                                </div>
                                              </td>
                                              <td style={{ textAlign: 'center', padding: '8px' }}>
                                                <input
                                                  type="number"
                                                  min="0"
                                                  max="100"
                                                  value={subEffective.rate.baseDiscount ?? subEffective.rate.tier1 ?? 15}
                                                  onChange={(e) => handleCategoryRateChange(String(sub.id), 'baseDiscount', e.target.value)}
                                                  title="Ulja Bazë (pa pajisje)"
                                                  style={{
                                                    width: 52,
                                                    padding: '4px 6px',
                                                    border: `1px solid ${subEffective.isCustom ? '#15803d' : '#cbd5e1'}`,
                                                    borderRadius: 6,
                                                    textAlign: 'center',
                                                    fontWeight: 700,
                                                    color: '#15803d',
                                                    background: subEffective.isCustom ? '#f0fdf4' : '#fff'
                                                  }}
                                                />
                                              </td>
                                              <td style={{ textAlign: 'center', padding: '8px' }}>
                                                <input
                                                  type="number"
                                                  min="0"
                                                  max="100"
                                                  value={subEffective.rate.fullDiscount ?? subEffective.rate.tier2 ?? 30}
                                                  onChange={(e) => handleCategoryRateChange(String(sub.id), 'fullDiscount', e.target.value)}
                                                  title="Ulja e Plotë (me pajisje)"
                                                  style={{
                                                    width: 52,
                                                    padding: '4px 6px',
                                                    border: `1px solid ${subEffective.isCustom ? '#0369a1' : '#cbd5e1'}`,
                                                    borderRadius: 6,
                                                    textAlign: 'center',
                                                    fontWeight: 700,
                                                    color: '#0369a1',
                                                    background: subEffective.isCustom ? '#f0f9ff' : '#fff'
                                                  }}
                                                />
                                              </td>
                                              <td style={{ textAlign: 'center', padding: '8px' }}>
                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                  {subEffective.isCustom ? (
                                                    <>
                                                      <span style={{ fontSize: 10, background: '#dcfce7', color: '#166534', padding: '2px 5px', borderRadius: 4, fontWeight: 700 }}>
                                                        Personalizuar
                                                      </span>
                                                      <button
                                                        type="button"
                                                        onClick={() => handleResetCategoryRate(String(sub.id), sub.name)}
                                                        title="Hiq personalizimin dhe trashëgo nga prindi"
                                                        style={{
                                                          background: 'transparent',
                                                          border: 'none',
                                                          color: '#dc2626',
                                                          cursor: 'pointer',
                                                          fontSize: 12,
                                                          padding: 2
                                                        }}
                                                      >
                                                        ✕
                                                      </button>
                                                    </>
                                                  ) : (
                                                    <span style={{ fontSize: 11, color: '#94a3b8' }}>
                                                      ↳ {subEffective.source === 'parent' ? 'nga prindi' : 'nga default'}
                                                    </span>
                                                  )}
                                                  {hasSubChildren && (
                                                    <button
                                                      type="button"
                                                      onClick={() => handleApplyToDescendants(subItem)}
                                                      title="Apliko te të gjitha nënkategoritë e kësaj"
                                                      style={{
                                                        marginLeft: 4,
                                                        background: 'transparent',
                                                        border: '1px solid #cbd5e1',
                                                        borderRadius: 4,
                                                        fontSize: 10,
                                                        padding: '2px 5px',
                                                        cursor: 'pointer',
                                                        color: '#0284c7'
                                                      }}
                                                    >
                                                      ⚡
                                                    </button>
                                                  )}
                                                </div>
                                              </td>
                                            </tr>

                                            {/* Level 2 Grandchild subcategories dropdown */}
                                            {hasSubChildren && subExpanded && (
                                              <tr>
                                                <td colSpan={4} style={{ padding: '8px 12px 12px 32px', background: '#f1f5f9' }}>
                                                  <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }}>
                                                    Nënkategoritë e "{sub.name}" ({subItem.subcategories.length}):
                                                  </div>
                                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8 }}>
                                                    {subItem.subcategories.map(grandChild => {
                                                      const gcEffective = getEffectiveCategoryRate(String(grandChild.id));
                                                      return (
                                                        <div
                                                          key={grandChild.id}
                                                          style={{
                                                            background: '#fff',
                                                            border: gcEffective.isCustom ? '1px solid #86efac' : '1px solid #e2e8f0',
                                                            borderRadius: 6,
                                                            padding: '6px 10px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            gap: 6
                                                          }}
                                                        >
                                                          <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ fontWeight: 600, fontSize: 12, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                              {grandChild.name}
                                                            </div>
                                                            <div style={{ fontSize: 10, color: '#94a3b8' }}>
                                                              ID: {grandChild.id}
                                                            </div>
                                                          </div>
                                                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                            <input
                                                              type="number"
                                                              min="0"
                                                              max="100"
                                                              value={gcEffective.rate.baseDiscount ?? gcEffective.rate.tier1 ?? 15}
                                                              onChange={(e) => handleCategoryRateChange(String(grandChild.id), 'baseDiscount', e.target.value)}
                                                              title="Ulja Bazë (%)"
                                                              style={{ width: 44, padding: '2px 4px', fontSize: 11, textAlign: 'center', fontWeight: 700, borderRadius: 4, border: '1px solid #cbd5e1', color: '#15803d' }}
                                                            />
                                                            <input
                                                              type="number"
                                                              min="0"
                                                              max="100"
                                                              value={gcEffective.rate.fullDiscount ?? gcEffective.rate.tier2 ?? 30}
                                                              onChange={(e) => handleCategoryRateChange(String(grandChild.id), 'fullDiscount', e.target.value)}
                                                              title="Ulja e Plotë (%)"
                                                              style={{ width: 44, padding: '2px 4px', fontSize: 11, textAlign: 'center', fontWeight: 700, borderRadius: 4, border: '1px solid #cbd5e1', color: '#0369a1' }}
                                                            />
                                                            {gcEffective.isCustom && (
                                                              <button
                                                                type="button"
                                                                onClick={() => handleResetCategoryRate(String(grandChild.id), grandChild.name)}
                                                                title="Hiq personalizimin"
                                                                style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 11, padding: 2 }}
                                                              >
                                                                ✕
                                                              </button>
                                                            )}
                                                          </div>
                                                        </div>
                                                      );
                                                    })}
                                                  </div>
                                                </td>
                                              </tr>
                                            )}
                                          </React.Fragment>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls Bar */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
            marginTop: 18,
            paddingTop: 16,
            borderTop: '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: 13, color: '#64748b', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span>
                Duke shfaqur <strong>{totalItems === 0 ? 0 : startIndex + 1}–{endIndex}</strong> nga gjithsej <strong>{totalItems}</strong> kategori
                {viewMode === 'hierarchy' && !selectedParentId && !catSearch && !selectedSubId && (
                  <span> kryesore (<strong>{allCategoriesFlat.length}</strong> gjithsej me nënkategoritë)</span>
                )}
              </span>

              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 8 }}>
                <span>Për faqe:</span>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  style={{
                    padding: '4px 8px',
                    borderRadius: 6,
                    border: '1px solid #cbd5e1',
                    fontSize: 12,
                    fontWeight: 600,
                    background: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={0}>Të gjitha</option>
                </select>
              </div>
            </div>

            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button
                  type="button"
                  disabled={safeCurrentPage <= 1}
                  onClick={() => handlePageChange(safeCurrentPage - 1)}
                  style={{
                    padding: '5px 12px',
                    borderRadius: 6,
                    border: '1px solid #cbd5e1',
                    background: safeCurrentPage <= 1 ? '#f8fafc' : '#fff',
                    color: safeCurrentPage <= 1 ? '#94a3b8' : '#334155',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: safeCurrentPage <= 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  ◀ Para
                </button>

                {renderPaginationButtons()}

                <button
                  type="button"
                  disabled={safeCurrentPage >= totalPages}
                  onClick={() => handlePageChange(safeCurrentPage + 1)}
                  style={{
                    padding: '5px 12px',
                    borderRadius: 6,
                    border: '1px solid #cbd5e1',
                    background: safeCurrentPage >= totalPages ? '#f8fafc' : '#fff',
                    color: safeCurrentPage >= totalPages ? '#94a3b8' : '#334155',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: safeCurrentPage >= totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  Pas ▶
                </button>
              </div>
            )}
          </div>

        </div>

        {/* Card 4: Specific Product Overrides */}
        <div className="admin-card" style={{ padding: 24, background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0' }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700, color: '#1e293b' }}>
            🏷️ Përjashtime Specifike për Produkte Individuale (Overrides)
          </h3>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>
            Përcaktoni përqindje të personalizuara për produkte specifike (sipas kodit SKU), të cilat mbishkruajnë normat e kategorisë.
          </p>

          <form onSubmit={handleAddOverride} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16, background: '#f8fafc', padding: 14, borderRadius: 10, border: '1px solid #e2e8f0' }}>
            <input
              type="text"
              className="admin-input"
              style={{ width: 220 }}
              placeholder="Kodi SKU (p.sh. B2TVL43...)"
              value={overrideSku}
              onChange={(e) => setOverrideSku(e.target.value)}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#15803d' }}>Ulja Bazë:</span>
              <input
                type="number"
                min="0"
                max="100"
                style={{ width: 60, padding: '4px 6px', borderRadius: 6, border: '1px solid #cbd5e1', textAlign: 'center', fontWeight: 700, color: '#15803d' }}
                value={overrideBase}
                onChange={(e) => setOverrideBase(e.target.value)}
              />
              <span style={{ fontSize: 12, color: '#94a3b8' }}>%</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#0369a1' }}>Ulja e Plotë:</span>
              <input
                type="number"
                min="0"
                max="100"
                style={{ width: 60, padding: '4px 6px', borderRadius: 6, border: '1px solid #cbd5e1', textAlign: 'center', fontWeight: 700, color: '#0369a1' }}
                value={overrideFull}
                onChange={(e) => setOverrideFull(e.target.value)}
              />
              <span style={{ fontSize: 12, color: '#94a3b8' }}>%</span>
            </div>

            <button
              type="submit"
              style={{
                background: '#007da5',
                color: '#fff',
                border: 'none',
                padding: '6px 16px',
                borderRadius: 8,
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer'
              }}
            >
              + Shto Mbishkrim
            </button>
          </form>

          <div>
            {Object.keys(campaign.productOverrides || {}).length === 0 ? (
              <div style={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic' }}>
                Nuk ka asnjë mbishkrim specifik për produkte. Të gjitha produktet përdorin normat e kategorisë përkatëse.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
                {Object.entries(campaign.productOverrides || {}).map(([key, rates]) => (
                  <div
                    key={key}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      background: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      padding: '10px 14px',
                      borderRadius: 10
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#166534' }}>{key}</div>
                      <div style={{ fontSize: 12, color: '#15803d', marginTop: 2 }}>
                        Bazë: <strong>{rates.baseDiscount ?? rates.tier1}%</strong> | Plotë: <strong>{rates.fullDiscount ?? rates.tier2}%</strong>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveOverride(key)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#dc2626',
                        cursor: 'pointer',
                        fontSize: 14,
                        padding: 4
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

      </div>
      )}
    </div>
  );
}
